function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

(() => {
  const categoryRedirects = {
    '/collections/cleansers': '/collections/all/orlena-category-cleansers',
    '/collections/toners-essences': '/collections/all/orlena-category-toners-essences',
    '/collections/serums-ampoules': '/collections/all/orlena-category-serums-ampoules',
    '/collections/creams-moisturizers': '/collections/all/orlena-category-creams-moisturizers',
    '/collections/eye-targeted-care': '/collections/all/orlena-category-eye-targeted-care',
    '/collections/masks-treatments': '/collections/all/orlena-category-masks-treatments',
    '/collections/spf-sun-care': '/collections/all/orlena-category-spf-sun-care',
    '/collections/devices-tools': '/collections/all/orlena-category-devices-tools',
  };
  const articleRedirects = {
    '/blogs/news/adaptogens-for-skin': '/blogs/the-deep-dive/adaptogens-for-skin',
    '/blogs/news/barrier-repair-after-chemical-peel': '/blogs/the-deep-dive/barrier-repair-after-chemical-peel',
    '/blogs/news/botox-aftercare-skincare': '/blogs/the-deep-dive/botox-aftercare-skincare',
    '/blogs/news/centella-vs-madecassoside': '/blogs/the-deep-dive/centella-vs-madecassoside',
    '/blogs/news/copper-peptides-explained': '/blogs/the-deep-dive/copper-peptides-explained',
    '/blogs/news/cortisol-and-skin-aging': '/blogs/the-deep-dive/cortisol-and-skin-aging',
    '/blogs/news/fermented-skincare-benefits': '/blogs/the-deep-dive/fermented-skincare-benefits',
    '/blogs/news/filler-aftercare': '/blogs/the-deep-dive/filler-aftercare',
    '/blogs/news/hair-thinning-vs-genetic-loss': '/blogs/the-deep-dive/hair-thinning-vs-genetic-loss',
    '/blogs/news/healing-pigmentation-after-procedures': '/blogs/the-deep-dive/healing-pigmentation-after-procedures',
    '/blogs/news/hormones-and-skin': '/blogs/the-deep-dive/hormones-and-skin',
    '/blogs/news/how-to-grow-hair-faster-naturally': '/blogs/the-deep-dive/copper-peptides-for-hair-growth',
    '/blogs/news/inflammaging-explained': '/blogs/the-deep-dive/inflammaging-explained',
    '/blogs/news/k-beauty-essential-ingredients': '/blogs/the-deep-dive/korean-skincare-philosophy',
    '/blogs/news/korean-skincare-hydration': '/blogs/the-deep-dive/korean-skincare-hydration',
    '/blogs/news/korean-skincare-layering': '/blogs/the-deep-dive/korean-skincare-layering',
    '/blogs/news/microbiome-skincare-explained': '/blogs/the-deep-dive/microbiome-skincare-explained',
    '/blogs/news/morning-shed-trend-explained': '/blogs/the-deep-dive/morning-shed-trend-explained',
    '/blogs/news/peptides-skincare-guide': '/blogs/the-deep-dive/peptides-skincare-guide',
    '/blogs/news/perimenopause-and-skin': '/blogs/the-deep-dive/estrogen-decline-and-skin',
    '/blogs/news/polynucleotides-pdrn-explained': '/blogs/the-deep-dive/polynucleotides-pdrn-explained',
    '/blogs/news/postbiotics-explained': '/blogs/the-deep-dive/postbiotics-explained',
    '/blogs/news/postpartum-hair-loss-explained': '/blogs/the-deep-dive/postpartum-hair-loss-explained',
    '/blogs/news/recovery-skincare-ingredients': '/blogs/the-deep-dive/recovery-skincare-ingredients',
    '/blogs/news/rosacea-skincare-guide': '/blogs/the-deep-dive/azelaic-acid-explained',
    '/blogs/news/scalp-health-explained': '/blogs/the-deep-dive/scalp-barrier-explained',
    '/blogs/news/signal-vs-carrier-peptides': '/blogs/the-deep-dive/signal-vs-carrier-peptides',
    '/blogs/news/skin-aging-guide': '/blogs/the-deep-dive/skin-aging-mid-thirties',
    '/blogs/news/skincare-routine-too-aggressive': '/blogs/the-deep-dive/skincare-routine-too-aggressive',
    '/blogs/news/sleep-and-skin-repair': '/blogs/the-deep-dive/sleep-and-skin-repair',
    '/blogs/news/wellness-and-skincare': '/blogs/the-deep-dive/adaptogens-for-skin',
    '/blogs/news/why-skin-looks-dull': '/blogs/the-deep-dive/why-skin-looks-dull',
    '/blogs/news/why-skin-suddenly-became-sensitive': '/blogs/the-deep-dive/why-skin-suddenly-became-sensitive',
    '/blogs/the-deep-dive/hydration-vs-moisture-skincare': '/blogs/the-deep-dive/hydration-vs-moisture-in-skincare',
    '/blogs/the-deep-dive/what-destroys-your-skin-barrier': '/blogs/the-deep-dive/what-destroys-your-skin-barrier-1',
    '/blogs/the-study/hyperpigmentation-dark-spots-treatment-guide': '/blogs/the-deep-dive/hyperpigmentation-dark-spots-treatment-guide',
    '/blogs/the-study/orean-skincare-philosophy-guide': '/blogs/the-deep-dive/korean-skincare-philosophy',
    '/blogs/the-study/post-treatment-skincare-recovery-protocol': '/blogs/the-deep-dive/post-treatment-skincare-recovery-protocol',
  };
  const productRedirects = {
    '/products/dr-drawing-cica-revival-ampoule': '/collections/barrier-recovery',
    '/products/dr-drawing-total-care-snail-mucin-ampoule': '/collections/barrier-recovery',
    '/products/dr-drawing-vitality-cream': '/collections/professional-supplies',
    '/products/dr-drawing-b-tox-peptide': '/collections/prevention-longevity',
    '/products/dr-drawing-sc-control-stem-cell-ampoule': '/collections/prevention-longevity',
    '/products/dr-drawing-stem-cell-ampoule': '/collections/prevention-longevity',
    '/products/dr-drawing-egf-brightening-ampoule': '/collections/brightening-support',
    '/products/fat-dissolving-injection-pine-bottle': '/collections/professional-body-contouring',
    '/products/dr-drawing-ac-control-salicylic-ampoule': '/collections/acne-inflammation',
    '/products/fat-dissolving-injection-peach-bottle': '/collections/professional-body-contouring',
    '/products/fat-dissolving-injection-body-contouring-silhouette-body': '/collections/professional-body-contouring',
    '/products/dr-drawing-salmon-dna-rejuvenation': '/collections/all/orlena-category-serums-ampoules',
    '/products/dr-drawing-sd-control-salmon-dna-ampoule': '/collections/all/orlena-category-serums-ampoules',
    '/products/dr-drawing-glow-reset-ampoule-collection': '/collections/all/orlena-category-serums-ampoules',
    '/products/dr-drawing-hy-control-hyaluronic-ampoule': '/collections/all/orlena-category-serums-ampoules',
    '/products/fat-dissolving-injection-body-contouring-lipolytic-solution': '/collections/professional-body-contouring',
    '/products/fat-dissolving-injection-lipolytic-solution': '/collections/professional-body-contouring',
  };
  const redirects = { ...categoryRedirects, ...articleRedirects, ...productRedirects };

  const cleanPath = window.location.pathname.replace(/\/$/, '');
  if (redirects[cleanPath]) {
    window.location.replace(redirects[cleanPath] + window.location.search + window.location.hash);
    return;
  }

  const rewriteCategoryLinks = () => {
    document.querySelectorAll('a[href]').forEach((link) => {
      try {
        const url = new URL(link.getAttribute('href'), window.location.origin);
        const path = url.pathname.replace(/\/$/, '');
        if (redirects[path]) {
          link.setAttribute('href', redirects[path]);
        }
      } catch (error) {
        // Ignore malformed or non-web links.
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', rewriteCategoryLinks, { once: true });
  } else {
    rewriteCategoryLinks();
  }
})();

(function () {
  if (window.__orlenaChatgptAdsTrackingLoaded) return;
  window.__orlenaChatgptAdsTrackingLoaded = true;

  var STORAGE_KEY = 'orlena_chatgpt_ads_attribution';
  var COOKIE_DAYS = 30;
  var CHATGPT_CAMPAIGNS = ['orlena_chatgpt_ads_june2026', 'orlena_chatgpt_ads_money_test_june2026', 'orlena_chatgpt_ads_glutanex_ampoule_june2026'];
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'];
  var ATTRIBUTE_PREFIX = 'chatgpt_ads_';
  var sentEvents = {};

  function nowIso() {
    return new Date().toISOString();
  }

  function readCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : '';
  }

  function writeCookie(name, value) {
    var expires = new Date(Date.now() + COOKIE_DAYS * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = name + '=' + encodeURIComponent(value) + '; expires=' + expires + '; path=/; SameSite=Lax';
  }

  function parseStored() {
    var raw = '';
    try {
      raw = window.localStorage.getItem(STORAGE_KEY) || '';
    } catch (error) {
      raw = '';
    }

    raw = raw || readCookie(STORAGE_KEY);
    if (!raw) return null;

    try {
      return JSON.parse(raw);
    } catch (error) {
      return null;
    }
  }

  function persist(record) {
    var value = JSON.stringify(record);
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch (error) {
      // Cookie storage below preserves attribution when localStorage is unavailable.
    }
    writeCookie(STORAGE_KEY, value);
  }

  function buildRecordFromUrl() {
    var params = new URLSearchParams(window.location.search);
    var utmSource = params.get('utm_source') || '';
    var utmCampaign = params.get('utm_campaign') || '';
    var isChatgptAds = utmSource === 'chatgpt_ads' || CHATGPT_CAMPAIGNS.indexOf(utmCampaign) !== -1;

    if (!isChatgptAds) return null;

    var existing = parseStored() || {};
    var record = {
      landing_page: existing.landing_page || window.location.href,
      first_touch_timestamp: existing.first_touch_timestamp || nowIso(),
      current_page: window.location.href
    };

    UTM_KEYS.forEach(function (key) {
      record[key] = params.get(key) || existing[key] || '';
    });

    return record;
  }

  function getAttribution() {
    var fromUrl = buildRecordFromUrl();
    if (fromUrl) {
      persist(fromUrl);
      return fromUrl;
    }

    var stored = parseStored();
    if (!stored) return null;
    stored.current_page = window.location.href;
    persist(stored);
    return stored;
  }

  function attributionPayload(extra) {
    var attribution = getAttribution();
    if (!attribution) return null;

    return Object.assign({
      traffic_source: 'chatgpt_ads',
      landing_page: attribution.landing_page,
      first_touch_timestamp: attribution.first_touch_timestamp,
      current_page: window.location.href,
      page_path: window.location.pathname,
      utm_source: attribution.utm_source || '',
      utm_medium: attribution.utm_medium || '',
      utm_campaign: attribution.utm_campaign || '',
      utm_content: attribution.utm_content || ''
    }, extra || {});
  }

  function sendEvent(name, params, onceKey) {
    var payload = attributionPayload(params);
    if (!payload) return;

    var key = onceKey || name + ':' + payload.page_path + ':' + (payload.utm_content || '');
    if (sentEvents[key]) return;
    sentEvents[key] = true;

    if (typeof window.gtag === 'function') {
      window.gtag('event', name, payload);
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: name }, payload));
  }

  function updateCartAttributes() {
    var attribution = getAttribution();
    if (!attribution || typeof window.fetch !== 'function') return;

    var attributes = {};
    UTM_KEYS.forEach(function (key) {
      attributes[ATTRIBUTE_PREFIX + key] = attribution[key] || '';
    });
    attributes[ATTRIBUTE_PREFIX + 'landing_page'] = attribution.landing_page || '';
    attributes[ATTRIBUTE_PREFIX + 'first_touch_timestamp'] = attribution.first_touch_timestamp || '';

    window.fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ attributes: attributes }),
      keepalive: true
    }).catch(function () {});
  }

  function ensureHiddenInput(form, name, value) {
    if (!form || !name) return;
    var input = form.querySelector('input[name="' + name + '"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value || '';
  }

  function hydrateAttributionForms() {
    var attribution = getAttribution();
    if (!attribution) return;

    var forms = document.querySelectorAll('form');
    Array.prototype.forEach.call(forms, function (form) {
      var formText = (form.getAttribute('class') || '') + ' ' + (form.getAttribute('id') || '') + ' ' + (form.getAttribute('action') || '');
      var hasEmail = form.querySelector('input[type="email"], input[name="contact[email]"], input[name="email"]');
      var looksLikeKlaviyo = /klaviyo|newsletter|customer|subscribe/i.test(formText);
      if (!hasEmail && !looksLikeKlaviyo) return;

      UTM_KEYS.forEach(function (key) {
        ensureHiddenInput(form, 'properties[' + ATTRIBUTE_PREFIX + key + ']', attribution[key] || '');
      });
      ensureHiddenInput(form, 'properties[' + ATTRIBUTE_PREFIX + 'landing_page]', attribution.landing_page || '');
      ensureHiddenInput(form, 'properties[' + ATTRIBUTE_PREFIX + 'first_touch_timestamp]', attribution.first_touch_timestamp || '');
    });
  }

  function cleanText(value) {
    return String(value || '').replace(/\s+/g, ' ').trim().slice(0, 120);
  }

  var attribution = getAttribution();
  if (!attribution) return;

  sendEvent('chatgpt_ads_landing_page_view', {}, 'chatgpt_ads_landing_page_view:' + window.location.pathname);

  if (window.location.pathname.indexOf('/products/') === 0) {
    sendEvent('view_item', {}, 'view_item:' + window.location.pathname);
  }

  if (window.location.pathname === '/pages/protocol-quiz') {
    sendEvent('protocol_quiz_start', { quiz_name: 'protocol_quiz' }, 'protocol_quiz_start:page');
  }

  hydrateAttributionForms();
  document.addEventListener('DOMContentLoaded', hydrateAttributionForms, { once: true });

  document.addEventListener('click', function (event) {
    var target = event.target;
    var link = target && target.closest ? target.closest('a[href]') : null;
    if (link) {
      var href = link.getAttribute('href') || '';
      if (href.indexOf('/pages/protocol-quiz') !== -1 || /quiz|pathway|routine/i.test(cleanText(link.textContent))) {
        sendEvent('protocol_quiz_start', {
          link_text: cleanText(link.textContent || link.getAttribute('aria-label')),
          destination_url: href
        }, 'protocol_quiz_start:' + href);
      }
    }

    var quizButton = target && target.closest ? target.closest('[data-quiz-next]') : null;
    if (quizButton && /see results/i.test(cleanText(quizButton.textContent))) {
      sendEvent('protocol_quiz_submit', { quiz_name: 'protocol_quiz' }, 'protocol_quiz_submit');
    }

    var addButton = target && target.closest ? target.closest('button[name="add"], [data-add-to-cart], .product-form__submit') : null;
    if (addButton) {
      updateCartAttributes();
      sendEvent('add_to_cart', { cart_action: 'button_click' }, 'add_to_cart:' + window.location.pathname);
    }
  }, true);

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('form')) return;

    hydrateAttributionForms();

    var action = form.getAttribute('action') || '';
    var hasEmail = form.querySelector('input[type="email"], input[name="contact[email]"], input[name="email"]');

    if (action.indexOf('/cart/add') !== -1) {
      updateCartAttributes();
      sendEvent('add_to_cart', { cart_action: 'form_submit' }, 'add_to_cart:' + window.location.pathname);
      return;
    }

    if (hasEmail) {
      sendEvent('email_signup', {
        form_id: form.id || '',
        form_type: form.querySelector('input[name="form_type"]')?.value || 'email'
      }, 'email_signup:' + window.location.pathname + ':' + (form.id || form.className || 'form'));
    }
  }, true);
})();

(() => {
  const staleCleanserImage = 'nexuspharma-glow-therapy-gel-to-foam-cleanser-1-2000x2000.webp';
  const pomelliCleanserImage = '//orlenaskin.com/cdn/shop/t/21/assets/orlena-pomelli-glutanex-glow-therapy-gel-to-foam-cleanser-bottle-v2.png';

  const replaceStaleCleanserImage = () => {
    document.querySelectorAll('img').forEach((image) => {
      if (!image.src.includes(staleCleanserImage) && !image.srcset.includes(staleCleanserImage)) return;

      image.src = pomelliCleanserImage;
      image.srcset = `${pomelliCleanserImage} 1254w`;
      image.width = 900;
      image.height = 900;
      image.alt = 'Glutanex Glow Therapy Gel-to-Foam Cleanser Pomelli-style bottle image for Orlena Skin';
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', replaceStaleCleanserImage, { once: true });
  } else {
    replaceStaleCleanserImage();
  }
})();

(() => {
  const replacements = new Map([
    ['Clinical Partnerships', 'Professional Partnerships'],
    ['clinical skincare', 'skincare guidance'],
    ['Clinical skincare', 'Skincare guidance'],
    ['Masks + Treatments', 'Masks + Targeted Care'],
    ['SERUMS AND TREATMENTS', 'KOREAN AMPOULE'],
    ['serums and treatment', 'lightweight Korean ampoule'],
    ['Hyperpigmentation Pathway', 'glow-focused pathway'],
    ['hyperpigmentation pathway', 'glow-focused pathway'],
    ['treatment-focused', 'glow-focused'],
    ['Treatment-focused', 'Glow-focused'],
    [
      'Orlena Skin is built on a protocol-driven approach to skin and wellness, combining professional K-beauty and clinical skincare with guided support for clarity, balance, and internal health beneath the surface.',
      'Orlena Skin is built on a protocol-driven approach to Korean skincare and modern wellness, combining curated formulas with guided support for clarity, balance, comfort, and visible skin quality.'
    ],
    [
      'Join for professional K-beauty, clinical skincare, protocol notes, and wellness guidance for long-term skin function.',
      'Join for Korean skincare, protocol notes, routine guidance, and modern wellness context for long-term skin clarity.'
    ],
    [
      'GLUTANEX GLOW THERAPY AMPOULE: SERUMS AND TREATMENTS FOR DULLNESS, UNEVEN TONE SUPPORT, POST-CLEANSE GLOW ROUTINES, HYDRATION SUPPORT',
      'GLUTANEX GLOW THERAPY AMPOULE: KOREAN AMPOULE FOR GLOW, HYDRATION, AND UNEVEN-LOOKING TONE'
    ],
    [
      "Glutanex Glow Therapy Ampoule is a serums and treatment selected for Orlena's Hyperpigmentation Pathway. It is best placed in routines focused on dullness, uneven tone support, post-cleanse glow routines, hydration support.",
      'Glutanex Glow Therapy Ampoule is a lightweight Korean ampoule selected for Orlena routines focused on dull-looking skin, visible tone concerns, hydration support, and a more radiant-looking complexion.'
    ],
    [
      'Customers who want the most treatment-focused step in the Glutanex Glow Therapy topical routine.',
      'Customers who want a glow-focused step in the Glutanex Glow Therapy topical routine.'
    ],
    [
      'Glutanex Glow Therapy Ampoule is best for customers who want the most treatment-focused step in the Glutanex Glow Therapy topical routine.',
      'Glutanex Glow Therapy Ampoule is best for customers who want a radiance-focused Korean skincare step with hydration support.'
    ]
  ]);

  const softenVisibleCopy = () => {
    const walker = document.createTreeWalker(document.body, 4);
    const textNodes = [];
    let node;

    while ((node = walker.nextNode())) {
      textNodes.push(node);
    }

    textNodes.forEach((textNode) => {
      let value = textNode.nodeValue;
      replacements.forEach((replacement, source) => {
        value = value.replaceAll(source, replacement);
      });
      textNode.nodeValue = value;
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', softenVisibleCopy, { once: true });
  } else {
    softenVisibleCopy();
  }

  if (window.MutationObserver) {
    const observer = new MutationObserver(softenVisibleCopy);
    const startObserver = () => {
      observer.observe(document.body, { childList: true, subtree: true });
      window.setTimeout(() => observer.disconnect(), 10000);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', startObserver, { once: true });
    } else {
      startObserver();
    }
  }
})();

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

(function () {
  if (window.location.pathname !== '/' || document.querySelector('.orlena-best-sellers-mock')) return;

  function ready(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  }

  ready(function () {
    if (document.querySelector('.orlena-best-sellers-mock')) return;

    var richTextSections = Array.prototype.slice.call(document.querySelectorAll('.shopify-section .rich-text'));
    var firstText = richTextSections.find(function (section) {
      return section.textContent && section.textContent.toLowerCase().indexOf('care that moves with the skin') !== -1;
    });

    if (!firstText) return;

    var hostSection = firstText.closest('.shopify-section');
    if (!hostSection || document.querySelector('.orlena-best-sellers-mock')) return;

    var section = document.createElement('section');
    section.className = 'orlena-best-sellers-mock orlena-best-sellers-mock--fallback';
    section.innerHTML = [
      '<div class="page-width">',
      '<div class="orlena-best-sellers-mock__header">',
      '<p class="orlena-best-sellers-mock__eyebrow">Curated essentials</p>',
      '<h2>Best Sellers</h2>',
      '</div>',
      '<div class="orlena-best-sellers-mock__grid">',
      '<a class="orlena-best-sellers-mock__card" href="/collections/all"><div class="orlena-best-sellers-mock__image">O</div><p class="orlena-best-sellers-mock__label">Mock product</p><h3>Barrier Recovery Cream</h3><span>View product</span></a>',
      '<a class="orlena-best-sellers-mock__card" href="/collections/all"><div class="orlena-best-sellers-mock__image">O</div><p class="orlena-best-sellers-mock__label">Mock product</p><h3>Brightening Ampoule</h3><span>View product</span></a>',
      '<a class="orlena-best-sellers-mock__card" href="/collections/all"><div class="orlena-best-sellers-mock__image">O</div><p class="orlena-best-sellers-mock__label">Mock product</p><h3>PDRN Recovery Mask</h3><span>View product</span></a>',
      '<a class="orlena-best-sellers-mock__card" href="/collections/all"><div class="orlena-best-sellers-mock__image">O</div><p class="orlena-best-sellers-mock__label">Mock product</p><h3>Daily Cica SPF</h3><span>View product</span></a>',
      '</div>',
      '</div>'
    ].join('');

    var style = document.createElement('style');
    style.textContent = [
      '.orlena-best-sellers-mock{background:#fff;color:#282828;padding:36px 0 48px}',
      '.orlena-best-sellers-mock__header{text-align:center;margin:0 auto 3.2rem;max-width:72rem}',
      '.orlena-best-sellers-mock__eyebrow{margin:0 0 1rem;color:#7ea5c4;font-size:1.3rem;font-weight:600;letter-spacing:.22em;text-transform:uppercase}',
      '.orlena-best-sellers-mock__header h2{margin:0;color:#111;font-family:var(--font-heading-family);font-size:clamp(3.4rem,4.7vw,6.8rem);font-weight:400;line-height:1.05;text-transform:uppercase}',
      '.orlena-best-sellers-mock__grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:2rem}',
      '.orlena-best-sellers-mock__card{display:block;min-width:0;color:inherit;text-decoration:none}',
      '.orlena-best-sellers-mock__image{display:grid;aspect-ratio:4/5;place-items:center;width:100%;background:#f4f6f6;border:.1rem solid rgba(126,165,196,.42);color:#7ea5c4;font-family:var(--font-heading-family);font-size:clamp(5rem,8vw,9rem);line-height:1}',
      '.orlena-best-sellers-mock__label{margin:1.6rem 0 .6rem;color:#7a7a7a;font-size:1.2rem;letter-spacing:.16em;text-transform:uppercase}',
      '.orlena-best-sellers-mock__card h3{margin:0 0 .8rem;color:#282828;font-family:var(--font-body-family);font-size:1.7rem;font-weight:500;line-height:1.35}',
      '.orlena-best-sellers-mock__card span{color:#20333d;font-size:1.3rem;font-weight:600;letter-spacing:.12em;text-transform:uppercase}',
      '@media screen and (max-width:989px){.orlena-best-sellers-mock__grid{grid-template-columns:repeat(2,minmax(0,1fr))}}',
      '@media screen and (max-width:549px){.orlena-best-sellers-mock{padding:28px 0 40px}.orlena-best-sellers-mock__grid{gap:1.4rem}.orlena-best-sellers-mock__header{margin-bottom:2.4rem}.orlena-best-sellers-mock__header h2{font-size:clamp(3rem,12vw,4.4rem)}}'
    ].join('');

    document.head.appendChild(style);
    hostSection.insertAdjacentElement('afterend', section);
  });
})();
